import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage, FormattedDate } from 'react-intl';
import includes from 'lodash/includes';
import classNames from 'classnames';

import DangerousHTML from 'Components/Utility/DangerousHTML';
import SampleDownloads from 'Components/Utility/Sample/SampleDownloads';
import SampleAudio from 'Components/Utility/Sample/SampleAudio';
import SampleVideo from 'Components/Utility/Sample/SampleVideo';
import SystemRequirements from 'Components/Product/SystemRequirements/SystemRequirements';
import { getPublicationAge } from 'Components/Bundle/BundleCarousels/BundleCarouselHelper';


const PublicationDetails = ({
  product,
  bundleSlug,
  slugsToBlur,
}) => {
  const {
    artists,
    authors,
    downloads,
    lang,
    name,
    pages,
    files,
    platform_specs: requirements,
    platforms: systems,
    slug,
    isbn,
    video_duration: videoDuration,
    publishers,
    release,
    age,
    type,
    url,
    desc,
    descs,
  } = product;
  const languages = isEmpty(lang) ? ['English'] : lang;
  const publicationDescription = descs ? descs[window.locale] : null || desc;

  const publicationAge = getPublicationAge(age);

  let filteredDownloads = [];
  if (downloads) {
    filteredDownloads = downloads.filter(download => !download.is_sample);
  }

  let sampleDownload = null;
  if (downloads && Array.isArray(downloads)) {
    sampleDownload = downloads.find(download => download.is_sample);
  }

  const downloadTypes = [];
  filteredDownloads.forEach((download) => {
    download.files.forEach((file) => {
      if (includes(downloadTypes, file.format)) return;
      if (file.format === 'zip') return;
      downloadTypes.push(file.format);
    });
  });

  // This has been added for the Soundtrack Loops Bundle where a
  // group download was added as a single download
  if (isEmpty(downloadTypes) && bundleSlug === 'soundtrack-loops-bundle') {
    downloadTypes.push('wav');
  }
  if (isEmpty(downloadTypes) && bundleSlug === 'dark-fantasy-royalty-free-music-and-sfx-bundle') {
    downloadTypes.push('mp3');
  }
  if (isEmpty(downloadTypes) && bundleSlug === 'big-royalty-free-game-music-and-sounds-bundle') {
    downloadTypes.push('flac');
  }

  let videoDurationHours = 0;
  let videoDurationMinutes = 0;
  if (videoDuration > 0) {
    videoDurationHours = Math.floor(videoDuration / 60);
    videoDurationMinutes = Math.floor(videoDuration % 60);
  }

  return (
    <div className={classNames('content-container', { 'blur-content': slugsToBlur.includes(slug) })} id="carousel-content">
      <div className="comic-content-columns">
        <article className="left-column">
          <div className="product-name-container">
            <h3 className="product-name">
              {name}
            </h3>
          </div>
          <div className="downloads-container">
            {downloadTypes.sort().map(file =>
              <div key={file} className="comic-download mr-2">
                {file}
              </div>,
            )}
          </div>

          <div className="product-details">
            <DangerousHTML tag="p" html={publicationDescription} />
          </div>
        </article>

        <div className="right-column">
          {(type === 'comic' || type === 'book' || type === 'audio' || type === 'video' || type === 'software' || type === 'elearning') &&
          <React.Fragment>
            {sampleDownload && (type === 'comic' || type === 'book') &&
            <article>
              <h4 className="mb-3">
                <FormattedMessage id="bundle-publication-details.download-sample" defaultMessage="Preview Product" />
              </h4>
              <SampleDownloads download={sampleDownload} />
              <hr />
            </article>
                }
            {sampleDownload && type === 'audio' && !isEmpty(sampleDownload.files) &&
            <article>
              <h4 className="mb-3">
                <FormattedMessage id="bundle-publication-details.audio-sample" defaultMessage="Audio Sample" />
              </h4>
              <SampleAudio files={sampleDownload.files} />
              <hr />
            </article>
                }
            {sampleDownload && (type === 'video' || type === 'software' || type === 'elearning') && !isEmpty(sampleDownload.files) &&
            <article>
              <h4 className="mb-3">
                <FormattedMessage id="bundle-publication-details.Video-sample" defaultMessage="Video Sample" />
              </h4>
              <SampleVideo files={sampleDownload.files} />
              <hr />
            </article>
                }
          </React.Fragment>
            }

          <article>
            <h4 className="mb-3">
              {type === 'comic' &&
              <FormattedMessage id="bundle-publication-details.comic-details" defaultMessage="Comic Details" />
                }
              {type === 'book' &&
              <FormattedMessage id="bundle-publication-details.ebook-details" defaultMessage="eBook Details" />
                }
              {type === 'software' &&
              <FormattedMessage id="bundle-publication-details.software-details" defaultMessage="Software Details" />
                }
              {type === 'audio' &&
              <FormattedMessage id="bundle-publication-details.audio-details" defaultMessage="Audio Details" />
                }
              {type === 'video' &&
              <FormattedMessage id="bundle-publication-details.video-details" defaultMessage="Video Details" />
                }
            </h4>
            <div className="overview-container">
              {!isEmpty(requirements) &&
              <div className="link-container">
                <a href="#sysreq">
                  <FormattedMessage id="bundle-publication-details.system-requirements" defaultMessage="System Requirements" />
                </a>
              </div>
                }
              {!isEmpty(languages) &&
              <div className="language-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.language"
                    defaultMessage="{count, plural, one {Language} other {Languages}}"
                    values={{ count: languages && languages.length }}
                  />:&nbsp;
                </span>
                {languages.map(language =>
                  <span key={language} className="mr-2">
                    {language}
                  </span>,
                )}
              </div>
                }
              {!isEmpty(authors) &&
              <div className="authors-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.author"
                    defaultMessage="{count, plural, one {Author} other {Authors}}"
                    values={{ count: authors.length }}
                  />:&nbsp;
                </span>
                {authors.map(author =>
                  <span key={author} className="mr-2">
                    {author}
                  </span>,
                )}
              </div>
                }
              {!isEmpty(artists) &&
              <div className="artists-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.artist"
                    defaultMessage="{count, plural, one {Artist} other {Artists}}"
                    values={{ count: artists.length }}
                  />:&nbsp;
                </span>
                {artists.map(artist =>
                  <span key={artist} className="mr-2">
                    {artist}
                  </span>,
                )}
              </div>
                }
              {release && (type === 'book' || type === 'comic') && !bundleSlug.includes('warhammer') &&
              <div className="pages-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.release-date"
                    defaultMessage="Publication Date"
                  />:&nbsp;
                </span>
                <FormattedDate value={release} month="long" year="numeric" />
              </div>
                }
              {!isEmpty(publishers) &&
              <div className="publishers-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.publisher"
                    defaultMessage="{count, plural, one {Publisher} other {Publishers}}"
                    values={{ count: publishers.length }}
                  />:&nbsp;
                </span>
                {publishers.map(publisher =>
                  <span key={publisher} className="mr-2">
                    {publisher.name || publisher}
                  </span>,
                )}
              </div>
                }
              {!isEmpty(downloadTypes) &&
              <div className="formats-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.format"
                    defaultMessage="{count, plural, one {Format} other {Formats}}"
                    values={{ count: downloadTypes.length }}
                  />:&nbsp;
                </span>
                {downloadTypes.sort().map(file =>
                  <span key={file} className="mr-2 file-format">
                    {file}
                  </span>,
                )}
              </div>
                }
              {videoDuration > 0 &&
              <div className="pages-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.video-duration.title"
                    defaultMessage="Duration:"
                  />
                </span>&nbsp;
                {videoDurationHours > 0 &&
                <FormattedMessage
                  id="bundle-publication-details.video-duration.hours"
                  defaultMessage="{hours} Hours "
                  values={{
                    hours: videoDurationHours,
                  }}
                />
                    }
                {videoDurationMinutes > 0 &&
                <FormattedMessage
                  id="bundle-publication-details.video-duration.minutes"
                  defaultMessage="{minutes} Minutes "
                  values={{
                    minutes: videoDurationMinutes,
                  }}
                />
                    }
              </div>
                }
              {files &&
              <div className="pages-container">
                <span className="overview-title">
                  {type === 'comic' &&
                  <FormattedMessage id="bundle-publication-details.comicCount" defaultMessage="Comics" />
                      }
                  {type !== 'comic' &&
                  <FormattedMessage id="bundle-publication-details.fileCount" defaultMessage="Files" />
                      }
                  :&nbsp;
                </span>
                {files}
              </div>
                }
              {pages &&
              <div className="pages-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.pageCount"
                    defaultMessage="Pages"
                  />:&nbsp;
                </span>
                {pages}
              </div>
                }
              {isbn &&
              <div className="pages-container">
                <span className="overview-title">
                  <FormattedMessage
                    id="bundle-publication-details.isbn"
                    defaultMessage="ISBN"
                  />:&nbsp;
                </span>
                {isbn}
              </div>
                }
              {publicationAge > 0 && type === 'book' &&
              <div className="age-container">
                <FormattedMessage
                  id="bundle-publication-details.age-book"
                  defaultMessage="Suitable for readers aged {age}+"
                  values={{ age: publicationAge }}
                />
              </div>
                }
              {publicationAge > 0 && type === 'audio' &&
              <div className="age-container">
                <FormattedMessage
                  id="bundle-publication-details.age-audio"
                  defaultMessage="Suitable for ages {age}+"
                  values={{ age: publicationAge }}
                />
              </div>
                }
              {!isEmpty(url) &&
              <div className="link-container">
                <FormattedMessage
                  id="bundle-publication-details.link"
                  defaultMessage="For more information"
                />
                    &nbsp;
                <a href={url} target="_blank" rel="noopener noreferrer" className="brand-link">
                  <FormattedMessage
                    id="bundle-publication-details.link.url"
                    defaultMessage="click here."
                  />
                </a>
              </div>
                }
            </div>
          </article>
        </div>
      </div>

      {!isEmpty(requirements) &&
      <article>
        <hr id="sysreq" />
        <h4>
          <FormattedMessage id="bundle-publication-details.sys-rec" defaultMessage="System Requirements" />
        </h4>
        <SystemRequirements
          fullWidth
          systems={systems}
          requirements={requirements}
        />
      </article>
        }
    </div>
  );
};

PublicationDetails.propTypes = {
  product: PropTypes.shape({}),
  bundleSlug: PropTypes.string,
  slugsToBlur: PropTypes.arrayOf(PropTypes.string),
};

PublicationDetails.defaultProps = {
  product: {},
  bundleSlug: '',
  slugsToBlur: [],
};

export default PublicationDetails;
